/**
 * Redux actions for lists of assets.
 * @module redux/actions/assets
 */

import { createAction } from "redux-actions";
import { getPrimeStyles } from "./asset";
import AssetType from "../constants/assetTypes";
import {
  getAssets as apiGetAssets,
  toggleFavorite as apiToggleFavorite,
  getContexts as apiGetContexts,
  getFilterOptions as apiGetFilterOptions,
  getCategoryGroups as apiGetCategoryGroups
} from "../api/camio";
import AssetSearchUtils from "../util/assetSearchUtils";
import FilterOptions from "../constants/filterOptions";
import StringUtils from "../util/stringUtils";

const localstorage = window.localStorage;

export const LOAD_MORE_ASSETS = "ASSETS/LOAD_MORE_ASSETS";
export const CLEAR_ASSETS = "ASSETS/CLEAR_ASSETS";
export const ASSET_TYPE_CHANGED = "ASSETS/CHANGE_ASSET_SEARCH";
export const SEARCH_TYPE_CHANGED = "ASSETS/SEARCH_TYPE_CHANGED";
export const ACTIVE_VIEW_CHANGED = "ASSETS/SET_ACTIVE_VIEW";
export const CONTEXT_CHANGED = "ASSETS/CONTEXT_CHANGED";
export const CATEGORY_CHANGED = "ASSETS/CATEGORY_CHANGED";
export const CATEGORIES_CHANGED = "ASSETS/CATEGORIES_CHANGED";
export const FILTER_BY_CHANGED = "ASSETS/FILTER_BY_CHANGED";
export const SORT_BY_CHANGED = "ASSETS/SORT_BY_CHANGED";
export const SEARCH_TERMS_CHANGED = "ASSETS/SEARCH_TERMS_CHANGED";
export const SUBMIT_ASSETS_REQUEST = "ASSETS/SUBMIT_ASSETS_REQUEST";
export const ASSETS_REQUEST_FAILED = "ASSETS/ASSETS_REQUEST_FAILED";
export const REQUEST_CONTEXTS = "ASSETS/REQUEST_CONTEXTS";
export const RECEIVED_CONTEXTS = "ASSETS/RECEIVED_CONTEXTS";
export const REQUEST_FILTER_OPTIONS = "ASSETS/REQUEST_FILTER_OPTIONS";
export const RECEIVED_FILTER_OPTIONS = "ASSETS/RECEIVED_FILTER_OPTIONS";
export const FILTER_OPTIONS_REQUEST_FAILED =
  "ASSETS/FILTER_OPTIONS_REQUEST_FAILED";
export const CONTEXTS_REQUEST_FAILED = "ASSETS/CONTEXTS_REQUEST_FAILED";
export const REQUEST_CATEGORIES = "ASSETS/REQUEST_CATEGORIES";
export const RECEIVED_CATEGORIES = "ASSETS/RECEIVED_CATEGORIES";
export const CATEGORIES_REQUEST_FAILED = "ASSETS/CATEGORIES_REQUEST_FAILED";
export const RECEIVED_ASSETS = "ASSETS/RECEIVED_ASSETS";
export const TOGGLE_FAVORITE_SUCCESS = "ASSETS/TOGGLE_FAVORITE_SUCCESS";
export const PAGE_CHANGED = "ASSETS/PAGE_CHANGED";
export const RELOAD = "ASSETS/RELOAD";
export const TOGGLE_DETAILS_VIEW = "ASSETS/TOGGLE_DETAILS_VIEW";
export const SET_DEFAULT_CONTEXT = "ASSETS/SET_DEFAULT_CONTEXT";
export const SET_DEFAULT_CATEGORY = "ASSETS/SET_DEFAULT_CATEGORY";
export const SET_NCS_INFO = "ASSETS/SET_NCS_INFO";
export const SET_CATEGORY_GROUPS = "ASSETS/SET_CATEGORY_GROUPS";
export const RESTORE_STATE = "ASSETS/RESTORE_STATE";

export const clearAssets = createAction(CLEAR_ASSETS);
export const changeSearchType = searchType =>
  createAction(SEARCH_TYPE_CHANGED)({ searchType });
export const changeActiveView = activeView =>
  createAction(ACTIVE_VIEW_CHANGED)({ activeView });
export const changeCategory = (groupId, categoryId) =>
  createAction(CATEGORY_CHANGED)({ groupId, categoryId });
const changeCategories = (groupId, categoryIds) =>
  createAction(CATEGORIES_CHANGED)({ groupId, categoryIds });
export const changeFilterBy = filterBy =>
  createAction(FILTER_BY_CHANGED)({ filterBy });
export const changeSortBy = sortBy => createAction(SORT_BY_CHANGED)({ sortBy });
export const changePage = page => createAction(PAGE_CHANGED)({ page });
export const changeSearchTerms = searchTerms =>
  createAction(SEARCH_TERMS_CHANGED)({ searchTerms });

const submitAssetsRequest = createAction(SUBMIT_ASSETS_REQUEST);
const requestContexts = createAction(REQUEST_CONTEXTS);
const handleReceiveContextList = contextList =>
  createAction(RECEIVED_CONTEXTS)({ contextList });
const contextsRequestFailed = createAction(CONTEXTS_REQUEST_FAILED);
const requestCategories = createAction(REQUEST_CATEGORIES);
export const handleReceiveCategoryList = categoryList =>
  createAction(RECEIVED_CATEGORIES)({ categoryList });
export const setCategoryGroups = categories =>
  createAction(SET_CATEGORY_GROUPS)({ categories });
const categoriesRequestFailed = createAction(CATEGORIES_REQUEST_FAILED);
const handleAssetSearchResults = assetResponse =>
  createAction(RECEIVED_ASSETS)({
    assetList: assetResponse.assets,
    pageNumber: assetResponse.pageInfo.pageIndex,
    totalPages: assetResponse.pageInfo.totalPageCount
  });
const handleAssetRequestFailed = createAction(ASSETS_REQUEST_FAILED);
const handleToggleFavoriteSuccess = (id, favorite) =>
  createAction(TOGGLE_FAVORITE_SUCCESS)({ id, favorite });
export const handleFilterOptionsReceived = filterList =>
  createAction(RECEIVED_FILTER_OPTIONS)({ filterList });

const toggleDetailsView = () => createAction(TOGGLE_DETAILS_VIEW)();

export const setDefaultContext = context => (dispatch, getState) => {
  const contextList = getState().assets.contextList;
  const contextsIntialized = getState().assets.contextsInitialized;

  if (!contextsIntialized) {
    return dispatch(createAction(SET_DEFAULT_CONTEXT)({ context }));
  }
  console.debug(
    `Setting default context after initialization.  Attempting to change context to '${context}'`
  );
  const matchedContext = contextList.find(x => x.name === context);

  if (!matchedContext) {
    console.warn(
      `Context '${context}' not found in existing list of contexts.`
    );
    return;
  }
  return dispatch(changeContext(matchedContext.id));
};

const setNcsInfo = ncsInfo => dispatch =>
  dispatch(createAction(SET_NCS_INFO)({ ncsInfo }));

export function changeAssetType(assetType) {
  let searchType = "graphic";
  let activeView = 1;
  switch (assetType) {
    case AssetType.GRAPHIC_TEMPLATE:
      searchType = "graphic";
      activeView = 1;
      break;
    case AssetType.GRAPHIC_MOS_OBJECT:
      searchType = "graphic";
      activeView = 2;
      break;
    case AssetType.AUTOMATION:
      searchType = "automation";
      activeView = 1;
      break;
    case AssetType.STUDIO_CLIP:
      searchType = "clips";
      activeView = 1;
      break;
    case AssetType.SAVED_ITEM:
      searchType = "saveditem";
      activeView = 2;
      break;
    default:
      break;
  }

  return createAction(ASSET_TYPE_CHANGED)({
    assetType,
    searchType,
    activeView
  });
}

export const contextsLoaded = contextList => (dispatch, getState) => {
  if (contextList.length > 0) {
    let context = contextList[0].id;
    const defaultContext =
      getState().assets.defaultContext || getState().assets.ncsInfo.showType;
    if (defaultContext) {
      const matchedContext = contextList.find(x => x.name === defaultContext);
      if (matchedContext) {
        context = matchedContext.id;
      }
      dispatch(createAction(SET_DEFAULT_CONTEXT)({ context: null }));
    }
    dispatch(changeContext(context));
  }
  return dispatch(handleReceiveContextList(contextList));
};

export function getContexts(userId) {
  return dispatch => {
    dispatch(requestContexts());
    apiGetContexts(userId)
      .then(contextList => contextsLoaded(contextList))
      .catch(error => {
        console.error(error);
        return dispatch(contextsRequestFailed());
      });
  };
}

export const getFilterOptions = (userId, assetType) => dispatch => {
  dispatch(createAction(REQUEST_FILTER_OPTIONS)());
  apiGetFilterOptions(userId, AssetSearchUtils.translateAssetType(assetType))
    .then(filterList =>
      dispatch(handleFilterOptionsReceived(filterList.options))
    )
    .catch(error => {
      console.error(error);
      dispatch(createAction(FILTER_OPTIONS_REQUEST_FAILED)());
    });
};

export const changeContext = contextId => dispatch => {
  dispatch(getPrimeStyles(contextId));
  return dispatch(createAction(CONTEXT_CHANGED)({ contextId }));
};

export const getCategories = type => dispatch => {
  dispatch(requestCategories());
  const assetType = AssetSearchUtils.translateAssetTypeToCategoryAssetType(
    type
  );
  apiGetCategoryGroups({ assetType })
    .then(categoryGroups => {
      const defaultSearchOptions = {}; // Populate this with the state object.
      const categories = [];

      for (let i = 0; i < categoryGroups.length; i++) {
        const group = categoryGroups[i];
        group.groupElementName =
          group.mosTag || StringUtils.camelize(group.name);
        const defaultCategory = defaultSearchOptions[group.mosTag];
        group.selectedCategoryId =
          group.categories && group.categories.length > 0
            ? group.categories[0].id
            : null;
        if (defaultCategory) {
          const matchedCategory = group.categories.find(
            x => x.name === defaultCategory
          );
          group.selectedCategoryId = matchedCategory
            ? matchedCategory.id
            : group.selectedCategoryId;
        }
        categories.push(group);
      }

      return dispatch(setCategoryGroups(categories));
    })
    .catch(error => {
      console.error(error);
      return dispatch(categoriesRequestFailed());
    });
};

/**
 * Submits a request to the Luci backend service to retrieve assets
 * based on the current filtering options.
 * @param  {string} assetType Searches for templates with a specific asset type
 * @param  {bool} filterByContent Searches for templates that contain
 * replaceables with the specified categories
 */
export function getAssets(assetType, filterByContent) {
  return (dispatch, getState) => {
    const currentState = getState().assets;
    const contentState = getState().content;
    const replaceableCategoriesFilter =
      filterByContent && contentState.replaceableCategories.length > 0
        ? contentState.replaceableCategories
        : [];
    const replaceableTypes = filterByContent ? [contentState.assetType] : "";
    if (currentState.assetsLoading) return;
    dispatch(submitAssetsRequest());

    const filterByTranslation = AssetSearchUtils.translateFilterBy(
      currentState.filterBy
    );
    const sortByTranslation = AssetSearchUtils.translateSortBy(
      currentState.sortBy,
      currentState.filterBy === FilterOptions.FAVORITE
    );

    const payload = {
      searchType: currentState.searchType,
      activeView: currentState.activeView,
      assetType: "templates",
      categoryId: currentState.categoryId,
      categories: currentState.categoryList,
      timeRange: filterByTranslation.timeRange,
      contextId: currentState.contextId,
      pageNumber: currentState.pageNumber,
      searchTerms: currentState.searchTerms,
      filterBy: filterByTranslation.filterBy,
      sortBy: sortByTranslation.sortBy,
      sortDirection: sortByTranslation.sortDirection,
      maxAssets: currentState.maxAssets
    };

    if (filterByContent) {
      payload.replaceableCategories = replaceableCategoriesFilter;
      payload.replaceableTypes = replaceableTypes;
    }

    apiGetAssets(payload)
      .then(response => dispatch(handleAssetSearchResults(response)))
      .catch(error => {
        console.error(error);
        dispatch(clearAssets());
        return dispatch(handleAssetRequestFailed());
      });
  };
}

export function toggleFavorite(id, favorite) {
  return dispatch => {
    apiToggleFavorite(id, favorite)
      .then(() => dispatch(handleToggleFavoriteSuccess(id, favorite)))
      .catch(error => {
        console.error(error);
        return Promise.reject();
      });
  };
}

export function reload() {
  return dispatch => {
    dispatch(clearAssets());
    return dispatch(createAction(RELOAD)());
  };
}

export const saveState = () => (dispatch, getState) => {
  const currentState = getState().assets;
  return {
    contextId: currentState.contextId,
    categoryList: currentState.categoryList,
    filterBy: currentState.filterBy,
    assetType: currentState.assetType,
    searchType: currentState.searchType,
    activeView: currentState.activeView,
    timeRange: currentState.timeRange
  };
};

export const restoreState = state => (dispatch, getState) => {
  return dispatch(createAction(RESTORE_STATE)({ ...state.assets }));
};

export const actions = {
  getAssets,
  getContexts,
  getCategories,
  changeAssetType,
  changeContext,
  changeCategory,
  changeCategories,
  changeActiveView,
  changeSearchType,
  changeFilterBy,
  changeSortBy,
  changeSearchTerms,
  toggleFavorite,
  changePage,
  reload,
  getFilterOptions,
  toggleDetailsView,
  setDefaultContext,
  setNcsInfo
};



// WEBPACK FOOTER //
// ./app/actions/assets.js